import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiPut } from '../services/api';
import { fmtMin as _fmtMinUTC } from '../services/dates';
import './SuperAdminPage.css';

const ALL_MODULES = [
  { key: 'crm',           label: 'CRM' },
  { key: 'financiero',    label: 'Financiero' },
  { key: 'calculadora',   label: 'Calculadora' },
  { key: 'financiacion',  label: 'Financiación' },
  { key: 'automatizacion',label: 'Automatización' },
  { key: 'proformas',     label: 'Proformas' },
  { key: 'estadisticas',  label: 'Estadísticas' },
  { key: 'fichaje',       label: 'Fichaje' },
  { key: 'instaladores',  label: 'App Instaladores' },
  { key: 'bot_voz',       label: 'Bot de voz' },
];

// ── Helper: descarga un blob como archivo ──────────────────────────────────
async function downloadFromAPI(url, filename, token) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

function fmtDate(str) { return _fmtMinUTC(str); }

// ── Sección: Exportar todos los datos ─────────────────────────────────────
function ExportAllSection({ token }) {
  const [loading, setLoading] = useState(false);

  async function handleExport() {
    setLoading(true);
    try {
      const filename = `saas_crm_export_${new Date().toISOString().slice(0,10)}.json`;
      await downloadFromAPI(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/superadmin/export`, filename, token);
    } catch (e) {
      alert('Error al exportar: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sa-card">
      <div className="sa-card-head">
        <div className="sa-card-icon sa-icon--blue">
          <IcoExport />
        </div>
        <div>
          <h3 className="sa-card-title">Exportar todos los datos</h3>
          <p className="sa-card-desc">Descarga un JSON con todos los tenants, usuarios (sin contraseñas), leads, notas, presupuestos, facturas y estadísticas generales.</p>
        </div>
      </div>
      <div className="sa-card-body">
        <div className="sa-info-list">
          <span className="sa-info-item">Tenants y configuración</span>
          <span className="sa-info-item">Usuarios (sin contraseñas)</span>
          <span className="sa-info-item">Leads con notas y proformas</span>
          <span className="sa-info-item">Estadísticas generales</span>
        </div>
        <button className="sa-btn sa-btn--primary" onClick={handleExport} disabled={loading}>
          {loading ? 'Generando…' : 'Exportar JSON completo'}
        </button>
      </div>
    </div>
  );
}

// ── Sección: Exportar por tenant ───────────────────────────────────────────
function ExportTenantSection({ token }) {
  const [tenants,  setTenants]  = useState([]);
  const [selId,    setSelId]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [loadingT, setLoadingT] = useState(true);

  useEffect(() => {
    apiGet('/superadmin/tenants', token)
      .then(data => { setTenants(data); if (data.length) setSelId(String(data[0].id)); })
      .catch(() => {})
      .finally(() => setLoadingT(false));
  }, [token]);

  async function handleExport() {
    if (!selId) return;
    setLoading(true);
    try {
      const t = tenants.find(x => String(x.id) === selId);
      const safe = (t?.name || 'tenant').replace(/[^a-z0-9]/gi, '_');
      const filename = `${safe}_${new Date().toISOString().slice(0,10)}.csv`;
      await downloadFromAPI(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/superadmin/export/${selId}`,
        filename, token
      );
    } catch (e) {
      alert('Error al exportar: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sa-card">
      <div className="sa-card-head">
        <div className="sa-card-icon sa-icon--green">
          <IcoTenant />
        </div>
        <div>
          <h3 className="sa-card-title">Exportar por empresa</h3>
          <p className="sa-card-desc">Selecciona una empresa y descarga sus datos en CSV (leads, usuarios y presupuestos). Compatible con Excel.</p>
        </div>
      </div>
      <div className="sa-card-body">
        {loadingT ? (
          <p className="sa-loading-text">Cargando empresas…</p>
        ) : (
          <div className="sa-export-row">
            <select className="sa-select" value={selId} onChange={e => setSelId(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.subdomain})</option>)}
            </select>
            <button className="sa-btn sa-btn--success" onClick={handleExport} disabled={loading || !selId}>
              {loading ? 'Generando…' : 'Exportar CSV'}
            </button>
          </div>
        )}
        <div className="sa-info-list">
          <span className="sa-info-item">Leads del tenant</span>
          <span className="sa-info-item">Usuarios activos</span>
          <span className="sa-info-item">Presupuestos emitidos</span>
        </div>
      </div>
    </div>
  );
}

// ── Sección: Backups automáticos ───────────────────────────────────────────
function BackupsSection({ token }) {
  const [backups,  setBackups]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [dlId,     setDlId]     = useState(null);

  const loadBackups = useCallback(() => {
    apiGet('/superadmin/backups', token)
      .then(setBackups)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadBackups(); }, [loadBackups]);

  async function handleCreate() {
    setCreating(true);
    try {
      await apiPost('/superadmin/backups', { label: `Backup manual ${new Date().toLocaleString('es-ES')}` }, token);
      loadBackups();
    } catch (e) {
      alert('Error al crear backup: ' + e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleDownload(id, createdAt) {
    setDlId(id);
    try {
      const filename = `backup_${new Date(createdAt).toISOString().slice(0,10)}_${id}.json`;
      await downloadFromAPI(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/superadmin/backups/${id}/download`,
        filename, token
      );
    } catch (e) {
      alert('Error al descargar: ' + e.message);
    } finally {
      setDlId(null);
    }
  }

  return (
    <div className="sa-card sa-card--full">
      <div className="sa-card-head sa-card-head--between">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="sa-card-icon sa-icon--orange">
            <IcoBackup />
          </div>
          <div>
            <h3 className="sa-card-title">Backups del sistema</h3>
            <p className="sa-card-desc">Se genera un backup automático cada vez que el superadmin inicia sesión. Máximo 30 guardados.</p>
          </div>
        </div>
        <button className="sa-btn sa-btn--primary" onClick={handleCreate} disabled={creating}>
          {creating ? 'Creando…' : '+ Crear backup ahora'}
        </button>
      </div>

      <div className="sa-card-body">
        {loading ? (
          <p className="sa-loading-text">Cargando backups…</p>
        ) : backups.length === 0 ? (
          <p className="sa-loading-text">No hay backups aún. Inicia sesión como superadmin o crea uno manualmente.</p>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Descripción</th>
                <th>Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b, i) => (
                <tr key={b.id}>
                  <td className="sa-table-id">{b.id}</td>
                  <td>
                    <span className={`sa-backup-label ${b.label?.includes('Auto') ? 'sa-backup-label--auto' : 'sa-backup-label--manual'}`}>
                      {b.label?.includes('Auto') ? 'Auto' : 'Manual'}
                    </span>
                    <span className="sa-backup-desc">{b.label}</span>
                  </td>
                  <td className="sa-table-date">{fmtDate(b.created_at)}</td>
                  <td>
                    <button
                      className="sa-btn-dl"
                      onClick={() => handleDownload(b.id, b.created_at)}
                      disabled={dlId === b.id}
                      title="Descargar backup"
                    >
                      {dlId === b.id ? '…' : <IcoDl />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Sección: Gestión de módulos por tenant ────────────────────────────────
function TenantsModulesSection({ token }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null); // tenant id que está guardando
  const [localMods, setLocalMods] = useState({}); // { [tenantId]: [modules] }

  useEffect(() => {
    apiGet('/tenants', token)
      .then(data => {
        setTenants(data);
        const map = {};
        data.forEach(t => { map[t.id] = t.modules || []; });
        setLocalMods(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  function toggleModule(tenantId, modKey) {
    setLocalMods(prev => {
      const current = prev[tenantId] || [];
      const next = current.includes(modKey)
        ? current.filter(m => m !== modKey)
        : [...current, modKey];
      return { ...prev, [tenantId]: next };
    });
  }

  async function saveModules(tenantId) {
    setSaving(tenantId);
    try {
      await apiPut(`/tenants/${tenantId}/modules`, { modules: localMods[tenantId] || [] }, token);
    } catch (e) {
      alert('Error al guardar: ' + e.message);
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="sa-card sa-card--full">
      <div className="sa-card-head">
        <div className="sa-card-icon sa-icon--purple"><IcoModules /></div>
        <div>
          <h3 className="sa-card-title">Módulos por empresa</h3>
          <p className="sa-card-desc">Activa o desactiva módulos para cada empresa. Los cambios se aplican inmediatamente al guardar.</p>
        </div>
      </div>
      <div className="sa-card-body">
        {loading ? (
          <p className="sa-loading-text">Cargando empresas…</p>
        ) : (
          <div className="sa-tenants-list">
            {tenants.map(t => (
              <div key={t.id} className="sa-tenant-row">
                <div className="sa-tenant-name">
                  <strong>{t.name}</strong>
                  <span className="sa-tenant-sub">{t.subdomain}</span>
                </div>
                <div className="sa-modules-grid">
                  {ALL_MODULES.map(mod => {
                    const active = (localMods[t.id] || []).includes(mod.key);
                    return (
                      <button
                        key={mod.key}
                        className={`sa-mod-btn ${active ? 'sa-mod-btn--on' : 'sa-mod-btn--off'}`}
                        onClick={() => toggleModule(t.id, mod.key)}
                      >
                        {active ? '✓' : '○'} {mod.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="sa-btn sa-btn--primary sa-btn--sm"
                  onClick={() => saveModules(t.id)}
                  disabled={saving === t.id}
                >
                  {saving === t.id ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────
const IcoExport = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
  </svg>
);
const IcoTenant = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M3 9h6M3 15h6"/>
  </svg>
);
const IcoBackup = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zM12 6v6l4 2"/>
  </svg>
);
const IcoDl = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
  </svg>
);
const IcoModules = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

// ── Page ───────────────────────────────────────────────────────────────────
export default function SuperAdminPage() {
  const { auth } = useContext(AuthContext);
  const token = auth.token;

  return (
    <div className="sa-page">
      <div className="sa-page-header">
        <h2 className="sa-page-title">Exportación de datos</h2>
        <p className="sa-page-sub">Gestiona las exportaciones y backups del sistema completo.</p>
      </div>

      <div className="sa-grid">
        <ExportAllSection token={token} />
        <ExportTenantSection token={token} />
      </div>

      <TenantsModulesSection token={token} />
      <BackupsSection token={token} />
    </div>
  );
}
